/**
 * Google My Business Publishing Utilities
 * 
 * This module handles publishing content to GMB through the Worker API
 * including posts, review replies, and question answers.
 */

import { supabase } from '../lib/supabase'
import {
  createPost as createGMBPost,
  updatePost as updateGMBPost,
  deletePost as deleteGMBPost,
  replyToReview,
  deleteReviewReply,
  answerQuestion,
  retryGMBCall,
  type GMBWorkerResponse
} from './gmbWorkerApi'

// ===== Type Definitions =====

export interface PublishResult {
  success: boolean
  id?: string
  error?: string
  details?: any
}

export interface PostDraft {
  locationId: string
  summary: string
  topicType: 'STANDARD' | 'EVENT' | 'OFFER' | 'PRODUCT'
  callToAction?: {
    actionType: 'BOOK' | 'ORDER' | 'SHOP' | 'LEARN_MORE' | 'SIGN_UP' | 'CALL'
    url?: string
  }
  mediaUrls?: string[]
  event?: {
    title: string
    startDate: Date
    endDate?: Date
  }
  offer?: {
    couponCode?: string
    redeemUrl?: string
    terms?: string
  }
  scheduledFor?: Date
}

export interface ReviewReplyDraft {
  reviewId: string
  replyText: string
  locationId: string
}

export interface QuestionAnswerDraft {
  questionId: string
  locationId: string
  answerText: string
}

// ===== Post Publishing =====

/**
 * Publish a new post to GMB
 */
export async function publishPost(draft: PostDraft): Promise<PublishResult> {
  try {
    // Validate draft
    if (!draft.summary || draft.summary.trim().length === 0) {
      return {
        success: false,
        error: 'Post summary is required'
      }
    }

    // Build post object for GMB API
    const postData: any = {
      languageCode: 'en',
      summary: draft.summary,
      topicType: draft.topicType
    }

    // Add call to action
    if (draft.callToAction) {
      postData.callToAction = draft.callToAction
    }

    // Add media
    if (draft.mediaUrls && draft.mediaUrls.length > 0) {
      postData.media = draft.mediaUrls.map(url => ({
        mediaFormat: 'PHOTO',
        sourceUrl: url
      }))
    }

    // Add event details
    if (draft.topicType === 'EVENT' && draft.event) {
      const startDate = draft.event.startDate
      const endDate = draft.event.endDate || draft.event.startDate

      postData.event = {
        title: draft.event.title,
        schedule: {
          startDate: {
            year: startDate.getFullYear(),
            month: startDate.getMonth() + 1,
            day: startDate.getDate()
          },
          endDate: {
            year: endDate.getFullYear(),
            month: endDate.getMonth() + 1,
            day: endDate.getDate()
          }
        }
      }
    }

    // Add offer details
    if (draft.topicType === 'OFFER' && draft.offer) {
      postData.offer = {
        couponCode: draft.offer.couponCode,
        redeemOnlineUrl: draft.offer.redeemUrl,
        termsConditions: draft.offer.terms
      }
    }

    // Create post via GMB Worker
    const result = await retryGMBCall(() => createGMBPost(draft.locationId, postData))

    if (result.success && result.data) {
      // Save to database
      await supabase
        .from('gmb_posts')
        .insert({
          gmb_post_id: result.data.name,
          gmb_location_id: draft.locationId,
          language_code: 'en',
          summary: draft.summary,
          topic_type: draft.topicType,
          state: result.data.state || 'LIVE',
          cta_type: draft.callToAction?.actionType,
          cta_url: draft.callToAction?.url,
          media_urls: draft.mediaUrls || [],
          event_title: draft.event?.title,
          event_start_date: draft.event?.startDate.toISOString(),
          event_end_date: draft.event?.endDate?.toISOString(),
          offer_coupon_code: draft.offer?.couponCode,
          offer_redeem_url: draft.offer?.redeemUrl,
          offer_terms: draft.offer?.terms,
          gmb_created_at: result.data.createTime,
          scheduled_for: draft.scheduledFor?.toISOString()
        })

      return {
        success: true,
        id: result.data.name,
        details: result.data
      }
    } else {
      return {
        success: false,
        error: result.error || 'Failed to create post'
      }
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

/**
 * Update an existing GMB post
 */
export async function updatePost(
  postId: string,
  updates: Partial<PostDraft>
): Promise<PublishResult> {
  try {
    // Get existing post from database
    const { data: existingPost, error: dbError } = await supabase
      .from('gmb_posts')
      .select('*')
      .eq('gmb_post_id', postId)
      .single()

    if (dbError || !existingPost) {
      return {
        success: false,
        error: 'Post not found in database'
      }
    }

    // Build update object
    const postData: any = {
      languageCode: 'en',
      summary: updates.summary || existingPost.summary,
      topicType: updates.topicType || existingPost.topic_type
    }

    // Update call to action
    if (updates.callToAction) {
      postData.callToAction = updates.callToAction
    } else if (existingPost.cta_type) {
      postData.callToAction = {
        actionType: existingPost.cta_type,
        url: existingPost.cta_url
      }
    }

    // Update media
    if (updates.mediaUrls) {
      postData.media = updates.mediaUrls.map(url => ({
        mediaFormat: 'PHOTO',
        sourceUrl: url
      }))
    } else if (existingPost.media_urls?.length > 0) {
      postData.media = existingPost.media_urls.map(url => ({
        mediaFormat: 'PHOTO',
        sourceUrl: url
      }))
    }

    // Update via GMB Worker
    const result = await retryGMBCall(() => updateGMBPost(postId, postData))

    if (result.success && result.data) {
      // Update database
      await supabase
        .from('gmb_posts')
        .update({
          summary: postData.summary,
          topic_type: postData.topicType,
          cta_type: postData.callToAction?.actionType,
          cta_url: postData.callToAction?.url,
          media_urls: updates.mediaUrls || existingPost.media_urls,
          gmb_updated_at: result.data.updateTime,
          synced_at: new Date().toISOString()
        })
        .eq('gmb_post_id', postId)

      return {
        success: true,
        id: postId,
        details: result.data
      }
    } else {
      return {
        success: false,
        error: result.error || 'Failed to update post'
      }
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

/**
 * Delete a GMB post
 */
export async function deletePost(postId: string): Promise<PublishResult> {
  try {
    // Delete via GMB Worker
    const result = await retryGMBCall(() => deleteGMBPost(postId))

    if (result.success) {
      // Update database (mark as removed)
      await supabase
        .from('gmb_posts')
        .update({
          state: 'REMOVED',
          synced_at: new Date().toISOString()
        })
        .eq('gmb_post_id', postId)

      return {
        success: true,
        id: postId
      }
    } else {
      return {
        success: false,
        error: result.error || 'Failed to delete post'
      }
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// ===== Review Reply Publishing =====

/**
 * Publish a reply to a GMB review
 */
export async function publishReviewReply(draft: ReviewReplyDraft): Promise<PublishResult> {
  try {
    // Validate draft
    if (!draft.replyText || draft.replyText.trim().length === 0) {
      return {
        success: false,
        error: 'Reply text is required'
      }
    }

    // Reply via GMB Worker
    const result = await retryGMBCall(() => 
      replyToReview(draft.reviewId, draft.replyText)
    )

    if (result.success) {
      // Update database
      const { data: { user } } = await supabase.auth.getUser()
      
      await supabase
        .from('gmb_reviews')
        .update({
          reply_comment: draft.replyText,
          reply_updated_at: new Date().toISOString(),
          replied_by: user?.id,
          needs_response: false,
          synced_at: new Date().toISOString()
        })
        .eq('gmb_review_id', draft.reviewId)

      // Log the reply in review responses table if it exists
      await supabase
        .from('review_responses')
        .insert({
          review_id: draft.reviewId,
          response_text: draft.replyText,
          published_to_google: true,
          published_at: new Date().toISOString(),
          created_by: user?.id || 'system'
        })
        .select()
        .single()

      return {
        success: true,
        id: draft.reviewId
      }
    } else {
      return {
        success: false,
        error: result.error || 'Failed to publish reply'
      }
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

/**
 * Delete a review reply
 */
export async function deleteReviewReply(reviewId: string): Promise<PublishResult> {
  try {
    // Delete via GMB Worker
    const result = await retryGMBCall(() => deleteReviewReply(reviewId))

    if (result.success) {
      // Update database
      await supabase
        .from('gmb_reviews')
        .update({
          reply_comment: null,
          reply_updated_at: null,
          replied_by: null,
          needs_response: true,
          synced_at: new Date().toISOString()
        })
        .eq('gmb_review_id', reviewId)

      return {
        success: true,
        id: reviewId
      }
    } else {
      return {
        success: false,
        error: result.error || 'Failed to delete reply'
      }
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// ===== Question Answer Publishing =====

/**
 * Publish an answer to a GMB question
 */
export async function publishQuestionAnswer(draft: QuestionAnswerDraft): Promise<PublishResult> {
  try {
    // Validate draft
    if (!draft.answerText || draft.answerText.trim().length === 0) {
      return {
        success: false,
        error: 'Answer text is required'
      }
    }

    // Answer via GMB Worker
    const result = await retryGMBCall(() => 
      answerQuestion(draft.locationId, draft.questionId, draft.answerText)
    )

    if (result.success && result.data) {
      // Update database
      const { data: { user } } = await supabase.auth.getUser()
      
      await supabase
        .from('gmb_questions')
        .update({
          answer_text: draft.answerText,
          answered_by: user?.id,
          answered_at: new Date().toISOString(),
          is_answered: true,
          gmb_updated_at: result.data.createTime,
          synced_at: new Date().toISOString()
        })
        .eq('gmb_question_id', draft.questionId)

      return {
        success: true,
        id: draft.questionId,
        details: result.data
      }
    } else {
      return {
        success: false,
        error: result.error || 'Failed to publish answer'
      }
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// ===== Batch Publishing =====

/**
 * Publish multiple review replies in batch
 */
export async function batchPublishReviewReplies(
  drafts: ReviewReplyDraft[]
): Promise<{ 
  successful: string[]
  failed: Array<{ id: string; error: string }>
}> {
  const successful: string[] = []
  const failed: Array<{ id: string; error: string }> = []

  // Process in batches with delay to avoid rate limits
  const batchSize = 5
  const delayMs = 2000

  for (let i = 0; i < drafts.length; i += batchSize) {
    const batch = drafts.slice(i, i + batchSize)
    
    // Process batch in parallel
    const results = await Promise.all(
      batch.map(draft => publishReviewReply(draft))
    )

    // Collect results
    batch.forEach((draft, index) => {
      const result = results[index]
      if (result.success) {
        successful.push(draft.reviewId)
      } else {
        failed.push({
          id: draft.reviewId,
          error: result.error || 'Unknown error'
        })
      }
    })

    // Delay between batches
    if (i + batchSize < drafts.length) {
      await new Promise(resolve => setTimeout(resolve, delayMs))
    }
  }

  return { successful, failed }
}

/**
 * Schedule a post for future publishing
 */
export async function schedulePost(
  draft: PostDraft,
  scheduledFor: Date
): Promise<PublishResult> {
  try {
    // Save draft to database for later publishing
    const { data: { user } } = await supabase.auth.getUser()
    
    const { data, error } = await supabase
      .from('gmb_posts')
      .insert({
        gmb_location_id: draft.locationId,
        language_code: 'en',
        summary: draft.summary,
        topic_type: draft.topicType,
        state: 'SCHEDULED',
        cta_type: draft.callToAction?.actionType,
        cta_url: draft.callToAction?.url,
        media_urls: draft.mediaUrls || [],
        event_title: draft.event?.title,
        event_start_date: draft.event?.startDate.toISOString(),
        event_end_date: draft.event?.endDate?.toISOString(),
        offer_coupon_code: draft.offer?.couponCode,
        offer_redeem_url: draft.offer?.redeemUrl,
        offer_terms: draft.offer?.terms,
        created_by: user?.id,
        scheduled_for: scheduledFor.toISOString()
      })
      .select()
      .single()

    if (error) throw error

    return {
      success: true,
      id: data.id,
      details: data
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// ===== Validation Helpers =====

/**
 * Validate post content before publishing
 */
export function validatePost(draft: PostDraft): { 
  valid: boolean
  errors: string[] 
} {
  const errors: string[] = []

  // Check summary
  if (!draft.summary || draft.summary.trim().length === 0) {
    errors.push('Post summary is required')
  } else if (draft.summary.length > 1500) {
    errors.push('Post summary must be 1500 characters or less')
  }

  // Check event details
  if (draft.topicType === 'EVENT') {
    if (!draft.event?.title) {
      errors.push('Event title is required for event posts')
    }
    if (!draft.event?.startDate) {
      errors.push('Event start date is required')
    }
  }

  // Check offer details
  if (draft.topicType === 'OFFER') {
    if (!draft.offer?.couponCode && !draft.offer?.redeemUrl) {
      errors.push('Offer must include either a coupon code or redeem URL')
    }
  }

  // Check media
  if (draft.mediaUrls && draft.mediaUrls.length > 10) {
    errors.push('Maximum 10 media items allowed per post')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Validate review reply before publishing
 */
export function validateReviewReply(draft: ReviewReplyDraft): { 
  valid: boolean
  errors: string[] 
} {
  const errors: string[] = []

  if (!draft.replyText || draft.replyText.trim().length === 0) {
    errors.push('Reply text is required')
  } else if (draft.replyText.length > 4096) {
    errors.push('Reply must be 4096 characters or less')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Validate question answer before publishing
 */
export function validateQuestionAnswer(draft: QuestionAnswerDraft): { 
  valid: boolean
  errors: string[] 
} {
  const errors: string[] = []

  if (!draft.answerText || draft.answerText.trim().length === 0) {
    errors.push('Answer text is required')
  } else if (draft.answerText.length > 1000) {
    errors.push('Answer must be 1000 characters or less')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}